import React, { useEffect, useState } from 'react';
import styles from '../unauthorized/ResetPasswordPage.module.scss';
import { Input } from '../../components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from '../../helpers/form/useAdvancedForm.ts';
import { StringParam, useQueryParams } from 'react-router-url-params';
import { useScopedTranslation } from '../../application/localization/useScopedTranslation.ts';
import { Button, ButtonColor } from '../../components/uikit/buttons/Button.tsx';
import { useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { UserClient } from '../../services/api/api-client';
import {
  LoadingPage,
  LoadingType,
} from '../../components/animations/LoadingPage.tsx';
import { validatePassword } from '../../helpers/password-helper.ts';
import { UnauthorizedPageRoot } from './UnauthorizedPageRoot.tsx';
import { AppLink } from '../../components/uikit/buttons/AppLink.tsx';
import Logo from 'assets/img/logo.svg?react';
import { useIsAuthorized } from '../../helpers/auth/auth-interceptor.ts';
import { requiredRule } from '../../helpers/form/react-hook-form-helper.ts';

const InvalidTokenString: string = 'Invalid token';

export type Form = {
  newPassword: string;
  confirmPassword: string;
};

export const ResetPasswordPage = () => {
  const i18n = useScopedTranslation('Page.ResetPassword');
  const i18nForgotPassword = useScopedTranslation('Page.ForgotPassword');
  const navigate = useNavigate();
  const [loadingStatus, setlLoadingStatus] = useState<LoadingType>('none');
  const [preLoadingStatus, setPreLoadingStatus] =
    useState<LoadingType>('loading');
  const [newRequestLoading, setNewRequestLoading] =
    useState<LoadingType>('none');
  const [params, _] = useQueryParams({
    username: StringParam,
    token: StringParam,
  });
  const isAuth = useIsAuthorized() === true;

  const handleBackToLogin = () => {
    setlLoadingStatus('none');
    void navigate('/');
  };
  const SendNewRequest = async () => {
    if (params.username)
      try {
        setNewRequestLoading('loading');
        await UserClient.sendResetPasswordEmail(params.username);
      } finally {
        setNewRequestLoading('completed');
      }
  };

  const form = useAdvancedForm<Form>(
    async () => {
      if (!params.username && !params.token) return;
      setlLoadingStatus('loading');
      try {
        await UserClient.resetPassword(
          params.username!,
          params.token!,
          newPassword!,
        );
        setlLoadingStatus('completed');
      } catch (err: any) {
        setlLoadingStatus('none');
        form.setError('newPassword', {
          message: err.detail,
        });
      }
    },
    { mode: 'onChange' },
  );
  const [newPassword, confirmPassword] = useWatch({
    control: form.control,
    name: ['newPassword', 'confirmPassword'],
  });

  const isEmptyParams = !params.username || !params.token;

  useEffect(() => {
    if (!isEmptyParams) {
      setPreLoadingStatus('loading');
      UserClient.resetPassword(params.username!, params.token!, '1')
        .then((e) => {
          setPreLoadingStatus('none');
        })
        .catch((err) => {
          if (err.detail.includes(InvalidTokenString))
            setPreLoadingStatus('completed');
          else setPreLoadingStatus('none');
        });
    }
  }, [params]);

  useEffect(() => {
    if (isEmptyParams) {
      void navigate('/');
    }
  }, [params, navigate]);

  const handleSubmit = async () => {
    const result = await form.trigger();
    if (!result) return;

    if (newPassword !== confirmPassword) {
      form.setError('confirmPassword', {
        message: i18n.t('password_mismatch'),
      });
      return;
    }

    void form.handleSubmitDefault();
  };
  const isFormShown =
    preLoadingStatus === 'none' &&
    loadingStatus === 'none' &&
    newRequestLoading === 'none';
  return (
    <>
      {!isFormShown && (
        <div className={styles.headerWrapper}>
          <AppLink to={'/'}>
            <div className={styles.lmtLogo}>
              <Logo />
            </div>
          </AppLink>
        </div>
      )}
      {newRequestLoading !== 'none' ? (
        <LoadingPage
          loadingStatus={newRequestLoading}
          titleLoadingContent={
            <div>{i18nForgotPassword.t('submit_loading_title')}</div>
          }
          titleContent={
            <div>{i18nForgotPassword.t('submit_completed_title')}</div>
          }
          bodyContent={
            <div>
              {i18nForgotPassword.t('submit_completed_text', {
                user_email: `${params.username}`,
              })}
            </div>
          }
          footerContent={
            <Button
              title={
                isAuth
                  ? i18n.t('authorized_go_home_button')
                  : i18nForgotPassword.t('submit_completed_button')
              }
              color={ButtonColor.Secondary}
              onClick={handleBackToLogin}
            />
          }
        />
      ) : preLoadingStatus !== 'none' ? (
        <LoadingPage
          afterLoading="failure"
          loadingStatus={preLoadingStatus}
          titleLoadingContent={
            preLoadingStatus === 'completed' && (
              <div>{i18n.t('submit_loading_title')}</div>
            )
          }
          titleContent={
            preLoadingStatus === 'completed' && (
              <div>{i18n.t('link_expired_title')}</div>
            )
          }
          bodyContent={
            preLoadingStatus === 'completed' && (
              <div style={{ whiteSpace: 'break-spaces' }}>
                {i18n.t('link_expired_text', {
                  user_email: `${params.username}`,
                })}
              </div>
            )
          }
          footerContent={
            preLoadingStatus === 'completed' && (
              <>
                <Button
                  title={
                    isAuth
                      ? i18n.t('authorized_go_home_button')
                      : i18n.t('link_expired_login_button')
                  }
                  color={ButtonColor.Secondary}
                  onClick={handleBackToLogin}
                />
                <Button
                  title={i18n.t('link_expired_request_button')}
                  color={ButtonColor.Primary}
                  onClick={SendNewRequest}
                />
              </>
            )
          }
        />
      ) : loadingStatus !== 'none' ? (
        <LoadingPage
          loadingStatus={loadingStatus}
          titleLoadingContent={<div>{i18n.t('submit_loading_title')}</div>}
          titleContent={<div>{i18n.t('submit_completed_title')}</div>}
          bodyContent={<div>{i18n.t('submit_completed_text')}</div>}
          footerContent={
            <Button
              title={
                isAuth
                  ? i18n.t('authorized_go_home_button')
                  : i18n.t('submit_completed_button')
              }
              color={ButtonColor.Primary}
              onClick={handleBackToLogin}
            />
          }
        />
      ) : (
        <>
          <UnauthorizedPageRoot>
            <div className={styles.loginTitle}>{i18n.t('title')}</div>
            <Input
              type="password"
              onEnterPressed={handleSubmit}
              {...form.register('newPassword', {
                ...requiredRule(i18n.t('required_error')),
                validate: (value) => validatePassword(value),
              })}
              className={styles.input}
              errorText={form.formState.errors.newPassword?.message}
              topHelperText={i18n.t('new_password')}
              fullWidth
            />
            <Input
              type="password"
              onEnterPressed={handleSubmit}
              {...form.register('confirmPassword', {
                ...requiredRule(i18n.t('required_error')),
                validate: (value) => validatePassword(value),
              })}
              className={styles.input}
              containerClassName={styles.inputContainer}
              errorText={form.formState.errors.confirmPassword?.message}
              topHelperText={i18n.t('confirm_password')}
              fullWidth
            />
            <Button
              onClick={handleSubmit}
              className={styles.button}
              title={i18n.t('submit_button')}
            />
          </UnauthorizedPageRoot>
        </>
      )}
    </>
  );
};
